import { handleAxiosError } from '@/api/helpers';
import { CampaignTemplate } from '@/client/campaigns';
import { DpaAcceptanceModal } from '@/components/campaigns/simulations/modals/DpaAcceptanceModal';
import { PlanWithAssistanceModal } from '@/components/campaigns/simulations/modals/PlanWithAssistanceModal';
import { useAccount } from '@/hooks/query';
import { useAcceptDpaAmendment } from '@/hooks/query/osint.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// UX: keep the "Data is being prepared" card visible for at least 5 seconds
// so fast transitions (preparing → complete/failed) don't flash too quickly
const MIN_PREPARING_DISPLAY_MS = 5_000;

type UseGenerateCustomizedSimulationParams = {
  isActive: boolean;
  onUseEmailTemplate?: (campaignTemplate?: CampaignTemplate) => void;
};

export const useGenerateCustomizedSimulation = ({
  isActive,
  onUseEmailTemplate,
}: UseGenerateCustomizedSimulationParams) => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const [shouldPoll, setShouldPoll] = useState(false);
  const { account: freshAccount, refetch: refetchAccount } = useAccount({
    accountId: currentAccount?.id,
    refetchInterval: shouldPoll ? 10_000 : false,
  });
  const { acceptDpa } = useAcceptDpaAmendment(currentAccount?.id ?? '');

  const [showAISimulationModal, setShowAISimulationModal] =
    useState<boolean>(false);
  const [showDpaAcceptanceModal, setShowDpaAcceptanceModal] =
    useState<boolean>(false);

  const preparingVisibleSince = useRef<number | null>(null);
  const [effectiveStatus, setEffectiveStatus] = useState<string | undefined>(
    freshAccount?.osintScrapingStatus,
  );

  useEffect(() => {
    const rawStatus = freshAccount?.osintScrapingStatus;

    if (rawStatus === 'in-progress') {
      if (!preparingVisibleSince.current) {
        preparingVisibleSince.current = Date.now();
      }
      setEffectiveStatus('in-progress');
      return;
    }

    if (preparingVisibleSince.current && rawStatus) {
      const elapsed = Date.now() - preparingVisibleSince.current;
      const remaining = MIN_PREPARING_DISPLAY_MS - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => {
          preparingVisibleSince.current = null;
          setEffectiveStatus(rawStatus);
        }, remaining);
        return () => clearTimeout(timer);
      }
    }

    preparingVisibleSince.current = null;
    setEffectiveStatus(rawStatus);
  }, [freshAccount?.osintScrapingStatus]);

  useEffect(() => {
    if (isActive) {
      refetchAccount();
    } else {
      setShouldPoll(false);
    }
  }, [isActive]);

  useEffect(() => {
    const inProgress =
      freshAccount?.osintEnabled &&
      freshAccount?.osintScrapingStatus === 'in-progress';
    setShouldPoll(isActive && !!inProgress);
  }, [isActive, freshAccount?.osintEnabled, freshAccount?.osintScrapingStatus]);

  const osintEnabled = !!freshAccount?.osintEnabled;
  const isPreparing = osintEnabled && effectiveStatus === 'in-progress';
  const isFailed = osintEnabled && effectiveStatus === 'failed';
  const showChip = !osintEnabled || effectiveStatus === 'complete';
  const isSubModalOpen = showDpaAcceptanceModal || showAISimulationModal;

  const handleLaunch = async () => {
    if (!freshAccount?.osintEnabled) {
      setShowDpaAcceptanceModal(true);
    } else if (effectiveStatus === 'failed') {
      try {
        await acceptDpa();
        await refetchAccount();
        toast?.success(t('toast.success'), t('osint.dpa.accepted.success'));
      } catch (error) {
        handleAxiosError(error as Error | AxiosError, toast);
      }
    } else {
      setShowAISimulationModal(true);
    }
  };

  const modals = (
    <>
      <DpaAcceptanceModal
        isOpen={showDpaAcceptanceModal}
        onClose={() => setShowDpaAcceptanceModal(false)}
        onAccepted={() => setShowDpaAcceptanceModal(false)}
        accountId={currentAccount?.id ?? ''}
      />
      <PlanWithAssistanceModal
        isOpen={showAISimulationModal}
        onClose={() => setShowAISimulationModal(false)}
        isSingleSimulation
        onUseEmailTemplate={(campaignTemplate?: CampaignTemplate) => {
          onUseEmailTemplate?.(campaignTemplate);
          setShowAISimulationModal(false);
        }}
      />
    </>
  );

  return {
    effectiveStatus,
    osintEnabled,
    isPreparing,
    isFailed,
    showChip,
    isSubModalOpen,
    handleLaunch,
    modals,
  };
};
